import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { METRICS } from '../data/metrics';
import type {
  ConnectedSourceId,
  DrillDownData,
  GroupKpis,
  Outlet,
} from '../types';
import { Icon, Spark, StatusDot, statusOf } from './shared/Icon';
import { DrillPanels } from './drill/DrillPanels';
import { DrillToolbar, DrillToolbarStat } from './shared/DrillToolbar';

function toneFor(
  key: string,
  value: number,
  avg: number,
): string {
  const m = METRICS[key];
  if (!m || !avg) return 'var(--ink-3)';
  const diff = (value - avg) / Math.abs(avg);
  const goodHigh = m.better === 'high';
  const good = goodHigh ? diff > 0.06 : diff < -0.06;
  const bad = goodHigh ? diff < -0.14 : diff > 0.22;
  if (bad) return 'var(--st-alert)';
  if (good) return 'var(--st-steady)';
  return 'var(--accent)';
}

export function DrillDown({
  data,
  outlets,
  onClose,
  onSelectOutlet,
}: {
  data: DrillDownData;
  outlets: Outlet[];
  kpis: GroupKpis;
  connected: ConnectedSourceId[];
  scopeLabel?: string;
  onClose: () => void;
  onSelectOutlet?: (id: string) => void;
}) {
  const { t } = useTranslation('performance');
  const m = METRICS[data.key];
  const label = m?.label ?? data.key;
  const [worstFirst, setWorstFirst] = useState(false);
  const open = outlets.filter(o => !o.closed);

  const rows = useMemo(() => {
    const getter = m?.get ?? (() => 0);
    const arr = open.map(o => ({ o, v: getter(o) }));
    const hi = m?.better === 'high';
    arr.sort((a, b) => (hi ? b.v - a.v : a.v - b.v));
    return worstFirst ? [...arr].reverse() : arr;
  }, [open, m, worstFirst]);

  // Only finite values set the scale: one NaN row would otherwise turn the
  // range label and every bar width into NaN.
  const vals = rows.map(r => r.v).filter(Number.isFinite);
  const maxV = Math.max(...vals, 0.0001);
  const minV = vals.length ? Math.min(...vals) : 0;
  const groupVal = data.groupValue;
  const goodHigh = m?.better === 'high';
  const trendPct = data.trendPct;
  const trendGood = trendPct != null && (goodHigh ? trendPct >= 0 : trendPct <= 0);

  const best = rows.length
    ? goodHigh
      ? rows.reduce((a, b) => (b.v > a.v ? b : a), rows[0])
      : rows.reduce((a, b) => (b.v < a.v ? b : a), rows[0])
    : null;
  const worst = rows.length
    ? goodHigh
      ? rows.reduce((a, b) => (b.v < a.v ? b : a), rows[0])
      : rows.reduce((a, b) => (b.v > a.v ? b : a), rows[0])
    : null;

  const avgRefX = (v: number) => Math.max(2, (v / maxV) * 100);
  const avgPct = avgRefX(
    m?.agg === 'sum' ? groupVal / (open.length || 1) : groupVal,
  );

  return (
    <div className="drill" data-ftue="live-drill">
      <DrillToolbar
        onClose={onClose}
        periodLabel={t('drill.today')}
        hero={
          <DrillToolbarStat
            label={m?.short ?? label}
            value={m ? m.fmt(data.groupValue) : data.groupValue}
            trend={
              data.trendLabel && trendPct != null ? (
                <div
                  className="drill-hero-d drill-toolbar-trend"
                  style={{
                    color: trendGood ? 'var(--st-steady)' : 'var(--st-alert)',
                  }}
                >
                  <Icon
                    name={trendPct >= 0 ? 'arrowUp' : 'arrowDown'}
                    size={13}
                    stroke={2.4}
                  />
                  {Math.abs(trendPct)}
                  {data.trendUnit ?? '%'} · {data.trendLabel}
                </div>
              ) : undefined
            }
          />
        }
      />

      <div className="drill-body">
        {data.perspectives && data.perspectives.length > 0 && (
          <div className="drill-persp">
            {data.perspectives.map(p => (
              <div className="dpsp-cell" key={p.label} title={p.hint}>
                <div className="dpsp-v">{p.value}</div>
                <div className="dpsp-l">
                  {p.label}
                  {p.scope && (
                    <span className={`kpi-scope scope-${p.scope}`}>
                      {t(`metrics:scope.${p.scope}.label`, { defaultValue: p.scope })}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {data.panels && data.panels.length > 0 && (
          <DrillPanels
            panels={data.panels}
            fmtVal={v => (m ? m.fmt(v) : String(v))}
            agentCtx={{ metricKey: data.key }}
          />
        )}

        <div className="drill-stats">
          <div className="ds-cell">
            <div className="ds-l">
              {m?.agg === 'sum' ? t('drill.groupTotal') : t('drill.groupAverage')}
            </div>
            <div className="ds-v">{m ? m.fmt(groupVal) : groupVal}</div>
          </div>
          <div className="ds-cell">
            <div className="ds-l">{t('drill.best', { name: best?.o.name ?? '-' })}</div>
            <div className="ds-v" style={{ color: 'var(--st-steady)' }}>
              {best ? (m ? m.fmt(best.v) : best.v) : '-'}
            </div>
          </div>
          <div className="ds-cell">
            <div className="ds-l">{t('drill.lowest', { name: worst?.o.name ?? '-' })}</div>
            <div className="ds-v" style={{ color: 'var(--st-alert)' }}>
              {worst ? (m ? m.fmt(worst.v) : worst.v) : '-'}
            </div>
          </div>
          <div className="ds-cell">
            <div className="ds-l">{t('drill.spread')}</div>
            <div className="ds-v">
              {m ? m.fmt(minV) : minV} – {m ? m.fmt(maxV) : maxV}
            </div>
          </div>
        </div>

        <div className={data.series.length ? 'drill-mid' : ''}>
          {data.series.length > 0 && (
            <div className="drill-panel">
              <div className="dp-head">
                <h3>{data.seriesLabel ?? t('drill.todayTitle')}</h3>
                <span>{t('drill.today')}</span>
              </div>
              <Spark data={data.series} w={640} h={120} />
            </div>
          )}
          <div className="drill-panel">
            <div className="dp-head">
              <h3>{t('drill.byRegion')}</h3>
              <span>{t('drill.regions', { count: data.byRegion.length })}</span>
            </div>
            <div className="region-bars">
              {data.byRegion.map(r => (
                <div className="rb-row" key={r.region}>
                  <span className="rb-name">{r.region}</span>
                  <span className="rb-track">
                    <span
                      className="rb-fill"
                      style={{
                        width: `${avgRefX(r.value)}%`,
                        background: toneFor(data.key, r.value, groupVal),
                      }}
                    />
                  </span>
                  <span className="rb-val">
                    {m ? m.fmt(r.value) : r.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="drill-panel">
          <div className="dp-head">
            <h3>{t('drill.everyOutlet')}</h3>
            <button
              type="button"
              className="cmp-sort"
              onClick={() => setWorstFirst(v => !v)}
            >
              <Icon name="sliders" size={13} />
              {worstFirst ? t('drill.worstFirst') : t('drill.bestFirst')}
            </button>
          </div>
          <div className="cmp-note">
            <span className="cmp-avgline" />
            {m?.agg === 'sum'
              ? t('drill.groupAvgPerOutlet')
              : t('drill.groupAverageRef')}
          </div>
          <div className="cmp-list">
            {rows.map((row, i) => {
              const rank = worstFirst ? rows.length - i : i + 1;
              const tone = toneFor(data.key, row.v, groupVal);
              return (
                <div
                  className="cmp-row"
                  key={row.o.id}
                  onClick={() => onSelectOutlet?.(row.o.id)}
                  role="button"
                >
                  <span className="cmp-rank">{rank}</span>
                  <div className="cmp-name">
                    <StatusDot status={statusOf(row.o)} />
                    <div>
                      <b>{row.o.name}</b>
                      <span>
                        {row.o.region} · {row.o.city}
                      </span>
                    </div>
                  </div>
                  <div className="cmp-bar">
                    <span
                      className="cmp-fill"
                      style={{
                        width: `${avgRefX(row.v)}%`,
                        background: tone,
                      }}
                    />
                    <span className="cmp-avg" style={{ left: `${avgPct}%` }} />
                  </div>
                  <span className="cmp-val" style={{ color: tone }}>
                    {m ? m.fmt(row.v) : row.v}
                    {data.key === 'covers' && row.o.upcoming > 0 && (
                      <span className="cmp-incoming" title={t('drill.incoming')}>
                        {t('drill.incomingIn', { count: row.o.upcoming })}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
